"use client";

import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import React, { useRef, useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { A11y, Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import StockCardSkeleton from "../ClpStockCards/StockCardSkeleton";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import { RecentlyViewedCard } from "./RecentlyViewedCard";
import { RecentlyViewedStockItem, RECENTLY_VIEWED_SKELETON_COUNT } from "./recentlyViewedTypes";

const SKELETON_CARD_COUNT = RECENTLY_VIEWED_SKELETON_COUNT;
const SKELETON_CARD_KEYS = Array.from(
  { length: SKELETON_CARD_COUNT },
  (_, index) => `RecentlyViewedGridSkeleton-${index + 1}`,
);

interface RecentlyViewedGridProps {
  stocks: RecentlyViewedStockItem[];
  errorMessage?: string;
  stockNotFoundImgUrl?: string;
  modalTitle?: string;
  modalBtnCta?: string;
  successMsg?: string;
  errorMsg?: string;
  nextArrowClassName?: string;
  isLoading?: boolean;
  onCardClick?: (stock: RecentlyViewedStockItem, position: string) => void;
  onCarouselClick?: (direction: "left" | "right") => void;
}

const RecentlyViewedGridSkeletonRow = () => (
  <div className="relative flex overflow-hidden">
    {SKELETON_CARD_KEYS.map((skeletonKey, index) => (
      <div
        key={skeletonKey}
        className={`min-w-[156px] max-w-[156px] md:min-w-[215px] md:max-w-[215px] ${
          index === SKELETON_CARD_COUNT - 1 ? "mr-1" : "mr-3 md:mr-4"
        }`}
      >
        <StockCardSkeleton showLogo={false} />
      </div>
    ))}
  </div>
);

export const RecentlyViewedGrid: React.FC<RecentlyViewedGridProps> = ({
  stocks,
  errorMessage,
  stockNotFoundImgUrl,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  nextArrowClassName,
  isLoading = false,
  onCardClick,
  onCarouselClick,
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [end, setEnd] = useState(false);
  const [start, setStart] = useState(true);

  let content;
  if (isLoading) {
    content = <RecentlyViewedGridSkeletonRow />;
  } else if (!stocks || stocks.length <= 0) {
    content = (
      <StockListNotFound
        errorMessage={errorMessage ?? ""}
        stockNotFoundImgUrl={stockNotFoundImgUrl ?? ""}
      />
    );
  } else {
    content = (
      <div className="relative overflow-hidden">
        {isMobile ? null : (
          <>
            <SwiperButton
              ref={prevRef}
              direction="prev"
              isDisabled={start}
              onClick={() => onCarouselClick?.("left")}
            />
            <SwiperButton
              ref={nextRef}
              direction="next"
              isDisabled={end}
              className={nextArrowClassName}
              onClick={() => onCarouselClick?.("right")}
            />
          </>
        )}

        <Swiper
          modules={[Navigation, Pagination, A11y]}
          spaceBetween={0}
          slidesPerView="auto"
          allowTouchMove
          observer
          observeParents
          navigation={
            isMobile
              ? false
              : {
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }
          }
          onBeforeInit={(swiper: SwiperType) => {
            if (isMobile) return;
            const navigationParams = swiper.params.navigation;
            if (!navigationParams || typeof navigationParams === "boolean") return;

            navigationParams.prevEl = prevRef.current;
            navigationParams.nextEl = nextRef.current;
          }}
          onInit={(swiper: SwiperType) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper: SwiperType) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onReachEnd={() => setEnd(true)}
          onFromEdge={() => setEnd(false)}
        >
          {stocks.map((stock, index) => (
            <SwiperSlide
              key={`RecentlyViewedGrid-${stock.symbol}-${index}`}
              className={`min-w-[156px]! max-w-[156px] md:min-w-[215px]! md:max-w-[215px] ${
                index === stocks.length - 1 ? "mr-1!" : "mr-3! md:mr-4!"
              }`}
            >
              <RecentlyViewedCard
                stock={stock}
                modalTitle={modalTitle}
                modalBtnCta={modalBtnCta}
                successMsg={successMsg}
                errorMsg={errorMsg}
                onCardClick={onCardClick}
                position={String(index + 1)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  return <div className="relative overflow-hidden">{content}</div>;
};
